import { differenceInDays, parseISO } from "date-fns";
import { PackageItemType } from "@/components/online-store/createPackage/packageItemRow";
import { HYPHEN } from "@/config";
import { MedspaOnlineStoreDataQuery } from "@/graphql/queries/onlineStore/medspaOnlineStoreData.graphql.types";
import { MedspaPackagesSettingsQuery } from "@/graphql/queries/settings/medspaPackagesSettings.graphql.types";
import { MedspaServicesAndProductsQuery } from "@/graphql/queries/settings/medspaServicesAndProducts.graphql.types";
import { formattedPrice, pluralize } from "@/utils";

export type PackageItemsType =
  MedspaPackagesSettingsQuery["medspaByPk"]["packages"][number]["packageItems"];

type SimplePackageItem = {
  itemKey: string;
  quantity: number;
};

const getQuantitySuffix = (quantity: number) =>
  quantity > 1 ? ` (${quantity})` : "";

// used in the creation process where service and product info are not nested objects
export const generateTempPackageItemString = (
  packageItems: SimplePackageItem[],
  services: MedspaServicesAndProductsQuery["medspaByPk"]["serviceMenuItems"],
  serviceProducts: MedspaServicesAndProductsQuery["medspaByPk"]["serviceProducts"],
  products: MedspaServicesAndProductsQuery["medspaByPk"]["products"]
) => {
  if (!packageItems?.length) return HYPHEN;

  const getItemString = (packageItem: SimplePackageItem) => {
    const [type, id] = packageItem.itemKey.split("-") as [
      PackageItemType,
      string,
    ];
    const quantitySuffix = getQuantitySuffix(packageItem.quantity);

    switch (type) {
      case PackageItemType.SERVICE:
        return (
          services.find((service) => service.id === id)?.name + quantitySuffix
        );
      case PackageItemType.SERVICE_PRODUCT:
        return (
          serviceProducts.find((serviceProduct) => serviceProduct.id === id)
            ?.serviceProduct.name + quantitySuffix
        );
      case PackageItemType.PRODUCT:
        return (
          products.find((product) => product.id === id)?.name + quantitySuffix
        );
      case PackageItemType.DOLLAR:
        return `${formattedPrice(packageItem.quantity)} Dollar Credit`;
      default:
        return type satisfies never;
    }
  };

  const packageItemStrings = packageItems.map(getItemString);

  return packageItemStrings.join(", ");
};

// used when we have the packageItems from the database where service and product are nested objects
export const generatePackageItemString = (packageItems: PackageItemsType) => {
  if (!packageItems?.length) return HYPHEN;

  const getItemString = (packageItem: PackageItemsType[number]) => {
    const type = packageItem.itemType as PackageItemType;
    const quantitySuffix = getQuantitySuffix(packageItem.itemAmount);

    switch (type) {
      case PackageItemType.SERVICE:
        return packageItem.serviceMenuItem.name + quantitySuffix;
      case PackageItemType.SERVICE_PRODUCT:
        return packageItem.serviceProduct.serviceProduct.name + quantitySuffix;
      case PackageItemType.PRODUCT:
        return packageItem.product.name + quantitySuffix;
      case PackageItemType.DOLLAR:
        return `${formattedPrice(
          packageItem.dollarCreditAmount
        )} Dollar Credit`;
      default:
        return type satisfies never;
    }
  };

  const packageItemStrings = packageItems.map(getItemString);

  return packageItemStrings.join(", ");
};

export const calculatePackageFullPrice = (
  packageItems: SimplePackageItem[],
  services: MedspaServicesAndProductsQuery["medspaByPk"]["serviceMenuItems"],
  serviceProducts: MedspaServicesAndProductsQuery["medspaByPk"]["serviceProducts"],
  products: MedspaServicesAndProductsQuery["medspaByPk"]["products"]
) => {
  if (!packageItems?.length) return 0;

  const getItemPrice = (packageItem: SimplePackageItem) => {
    const [type, id] = packageItem.itemKey.split("-") as [
      PackageItemType,
      string,
    ];

    switch (type) {
      case PackageItemType.SERVICE:
        return (
          parseFloat(services.find((service) => service.id === id)?.price) *
          packageItem.quantity
        );
      case PackageItemType.SERVICE_PRODUCT:
        return (
          parseFloat(
            serviceProducts.find((serviceProduct) => serviceProduct.id === id)
              ?.price
          ) * packageItem.quantity
        );
      case PackageItemType.PRODUCT:
        return (
          parseFloat(products.find((product) => product.id === id)?.price) *
          packageItem.quantity
        );
      case PackageItemType.DOLLAR:
        return packageItem.quantity;
      default:
        return type satisfies never;
    }
  };
  const fullPrice = packageItems.reduce(
    (acc, packageItem) => acc + getItemPrice(packageItem),
    0
  );

  return fullPrice;
};

type PackageItemOnlineStore =
  MedspaOnlineStoreDataQuery["medspa"][number]["packages"][number]["packageItems"][number];

export const generatePackageItemOnlineStoreName = (
  packageItem: PackageItemOnlineStore
) => {
  const itemType = packageItem.itemType as PackageItemType;

  switch (itemType) {
    case PackageItemType.SERVICE:
      return packageItem.serviceMenuItem.name;
    case PackageItemType.SERVICE_PRODUCT:
      return packageItem.serviceProduct.serviceProduct.name;
    case PackageItemType.PRODUCT:
      return packageItem.product.name;
    case PackageItemType.DOLLAR:
      return "Dollar deposit";
    default:
      return itemType satisfies never;
  }
};

export const generatePackageItemOnlineStoreAmount = (
  packageItem: PackageItemOnlineStore
) => {
  const itemType = packageItem.itemType as PackageItemType;
  let type = "";

  switch (itemType) {
    case PackageItemType.SERVICE:
      type = "session";
      break;
    case PackageItemType.SERVICE_PRODUCT:
      type = "unit";
      break;
    case PackageItemType.PRODUCT:
      type = "item";
      break;
    case PackageItemType.DOLLAR:
      return formattedPrice(packageItem.dollarCreditAmount);
    default:
      return itemType satisfies never;
  }

  return `${packageItem.itemAmount} ${pluralize(type, packageItem.itemAmount)}`;
};

const MIN_DAYS_DIFF_TO_FEATURE = 14;

export function isPackageFeatured(endDate: string | null | undefined): boolean {
  if (!endDate) return false;

  const differenceDays = differenceInDays(parseISO(endDate), new Date());

  return differenceDays <= MIN_DAYS_DIFF_TO_FEATURE && differenceDays > -1;
}

export const HUB_PAGE_QUERY_PARAM = "hub";
